import React, { FC, useCallback } from "react";
import {
  ArtifactV1SessionPart,
  NoteUpdatedV1ArtifactPayload,
  ArtifactV1SessionPartKind,
} from "../../session.types";
import { SessionPartConfig, SessionPartContext } from "../session-part.types";
import styled from "@emotion/styled";
import { NoteInfo } from "./session-artifact-note-renderer.utils";
import { DateTime } from "luxon";
import { MDSSessionArtifactNoteEmbed } from "@/mds/components/sessions/session-parts/artifacts/session-artifact-note-embed";
import { useIsLastPartInSessionGroup, useMDSSession } from "../../session-context";
import { MDSSessionArtifact } from "@/mds/components/sessions/session-context";
import { MDSBubble, MDSBubbleAlignment, MDSBubbleVariant } from "@/mds/components/bubble";
import { MemCommonMentionKind } from "@mem-labs/common-editor";

interface Props {
  part: ArtifactV1SessionPart;
  context: SessionPartContext;
  config: SessionPartConfig;
}

export const MDSSessionArtifactNoteUpdatedV1Part: FC<Props> = ({ part, context, config }) => {
  const payload = part.artifact_payload as NoteUpdatedV1ArtifactPayload;
  const { viewArtifact } = useMDSSession();

  const noteInfo: NoteInfo = {
    noteId: payload.note_id,
    createdAt: DateTime.fromISO(payload.note_created_at),
    updatedAt: DateTime.fromISO(payload.note_updated_at),
    encodedContent: payload.encoded_content,
    primaryLabel: payload.primary_label,
    // secondaryLabel: payload.secondary_label,
    secondaryLabel: "Updated Note",
    markdownContent: payload.markdown_content,
    versionNumber: payload.version,
  };

  const handleArtifactClick: React.MouseEventHandler<HTMLDivElement> = useCallback(
    event => {
      if (config.linkHandler) {
        config.linkHandler(MemCommonMentionKind.Note, payload.note_id, null, event, {
          noteVersion: payload.version,
        });
        return;
      }

      // Fallback to viewArtifact
      const artifactInfo: MDSSessionArtifact = {
        kind: ArtifactV1SessionPartKind.NoteUpdatedV1,
        payload,
      };

      viewArtifact({ payload: artifactInfo });
    },
    [config, payload, viewArtifact]
  );

  const isLastInGroup = useIsLastPartInSessionGroup({ part });

  return (
    <Bubble
      variant={MDSBubbleVariant.Transparent}
      align={MDSBubbleAlignment.Left}
      isLastInGroup={isLastInGroup}
    >
      <MDSSessionArtifactNoteEmbed
        context={context}
        config={config}
        noteInfo={noteInfo}
        onClick={handleArtifactClick}
      />
    </Bubble>
  );
};

const Bubble = styled(MDSBubble)<{ isLastInGroup?: boolean }>`
  margin-top: ${({ theme }) => theme.spacing["1"]};
  margin-bottom: ${({ theme }) => theme.spacing["4"]};

  > div {
    color: unset;
  }
`;
